import React from 'react';
import { Link } from 'react-router-dom';
import {
    ChartBarIcon,
    CpuChipIcon,
    CurrencyDollarIcon,
    BoltIcon,
    CheckCircleIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import NavbarPublic from '../components/NavbarPublic';

const LandingPage = () => {
    return (
        <div className="min-h-screen bg-white font-sans text-gray-900">
            <NavbarPublic />

            {/* Hero Section */}
            <div className="relative min-h-screen pt-32 pb-20 lg:pt-48 lg:pb-32 flex items-center overflow-hidden bg-gray-900 selection:bg-amber-500/30">
                {/* Dynamic Background */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1542314831-c6a4d1409e1c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                        alt="Luxury Dark Hotel"
                        className="w-full h-full object-cover opacity-20 mix-blend-luminosity"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/95 to-transparent"></div>

                    {/* Glowing Orbs */}
                    <div className="absolute top-1/4 -left-20 w-96 h-96 bg-amber-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-indigo-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-20" style={{ animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full flex flex-col lg:flex-row items-center justify-between">
                    <div className="lg:w-3/5 text-left pr-4">
                        <div className="inline-flex items-center px-4 py-2 bg-white/5 border border-white/10 rounded-full text-amber-400 text-sm font-bold mb-8 backdrop-blur-md shadow-lg shadow-amber-500/10 transform transition hover:scale-105 cursor-default group space-x-2">
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                            </span>
                            <span className="uppercase tracking-widest text-xs">AI-Powered Version 2.0 Live</span>
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.1] font-serif">
                            Stop Guessing. <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-200">Start Knowing.</span>
                        </h1>

                        <p className="max-w-xl text-xl text-gray-400 mb-10 leading-relaxed font-light border-l-2 border-amber-500/30 pl-6">
                            The ultimate Hospitality Intelligence framework. We predict inventory drains before they happen, eliminating F&B waste and skyrocketing margins.
                        </p>

                        <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4">
                            <Link to="/signup" className="group relative w-full sm:w-auto overflow-hidden rounded-lg bg-amber-500 px-10 py-4 font-bold text-gray-900 transition-all hover:bg-amber-400 hover:shadow-[0_0_40px_-10px_rgba(251,191,36,0.6)] border border-amber-400 text-lg uppercase tracking-widest flex justify-center items-center">
                                <span className="absolute right-0 translate-x-full transition-transform group-hover:-translate-x-4">
                                    <ArrowRightIcon className="h-5 w-5" />
                                </span>
                                <span className="transition-transform group-hover:-translate-x-4">Start Trial</span>
                            </Link>
                            <a href="#how-it-works" className="w-full sm:w-auto px-10 py-4 bg-white/5 text-white border border-white/20 rounded-lg font-bold text-lg hover:bg-white/10 transition-all flex items-center justify-center uppercase tracking-widest backdrop-blur-md">
                                View Demo
                            </a>
                        </div>

                        <div className="mt-12 flex items-center space-x-4">
                            <div className="flex -space-x-3">
                                <img className="w-10 h-10 rounded-full border-2 border-gray-900" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?fit=facearea&facepad=2&w=256&h=256&q=80" alt="Avatar" />
                                <img className="w-10 h-10 rounded-full border-2 border-gray-900" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?fit=facearea&facepad=2&w=256&h=256&q=80" alt="Avatar" />
                                <img className="w-10 h-10 rounded-full border-2 border-gray-900" src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?fit=facearea&facepad=2&w=256&h=256&q=80" alt="Avatar" />
                            </div>
                            <div className="text-sm">
                                <p className="text-white font-bold">Trusted by 500+ General Managers</p>
                                <p className="text-gray-400">Join the hospitality revolution.</p>
                            </div>
                        </div>

                    </div>

                    <div className="lg:w-2/5 hidden lg:block relative perspective-[1000px]">
                        <div className="relative transform rotate-y-[-10deg] rotate-x-[5deg] transition-transform duration-700 hover:rotate-y-0 hover:rotate-x-0 shadow-[0_0_50px_-10px_rgba(251,191,36,0.3)]">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-indigo-600 rounded-xl blur opacity-30 -inset-1"></div>
                            <div className="relative bg-gray-900 border border-gray-700/50 rounded-xl p-6 backdrop-blur-xl shadow-2xl w-[400px]">
                                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800">
                                    <h4 className="text-white font-bold flex items-center"><BoltIcon className="w-5 h-5 text-amber-400 mr-2" /> Live AI Insight</h4>
                                    <span className="text-[10px] text-amber-400 uppercase font-black tracking-widest flex items-center"><span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse mr-2"></span>Just Now</span>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed mb-6 font-light">
                                    <strong className="text-white font-bold">Attention:</strong> Cabernet Sauvignon stock is dropping 2x faster than normal. Predictive restock recommended before weekend influx.
                                </p>
                                <button className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg text-gray-900 font-bold text-sm hover:opacity-90 transition-opacity flex justify-center items-center shadow-lg shadow-amber-500/20 uppercase tracking-widest">
                                    Auto-Order Wholesale <CheckCircleIcon className="w-5 h-5 ml-2" />
                                </button>
                            </div>

                            {/* Floating sub-card */}
                            <div className="absolute -bottom-8 -left-12 bg-gray-900 border border-gray-700/50 rounded-xl p-5 shadow-2xl backdrop-blur-xl animate-bounce" style={{ animationDuration: '4s' }}>
                                <div className="flex items-center space-x-4">
                                    <div className="p-3 bg-green-500/10 rounded-lg text-green-500 border border-green-500/20">
                                        <ChartBarIcon className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Projected Savings</p>
                                        <p className="text-2xl text-white font-bold font-serif">$4,250</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="bg-amber-600 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    <div>
                        <div className="text-4xl font-bold text-white font-serif">15%</div>
                        <div className="text-amber-200 text-sm uppercase tracking-wider mt-1">Cost Reduction</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-white font-serif">20h+</div>
                        <div className="text-amber-200 text-sm uppercase tracking-wider mt-1">Monthly Hours Saved</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-white font-serif">0%</div>
                        <div className="text-amber-200 text-sm uppercase tracking-wider mt-1">Stockouts</div>
                    </div>
                    <div>
                        <div className="text-4xl font-bold text-white font-serif">24/7</div>
                        <div className="text-amber-200 text-sm uppercase tracking-wider mt-1">Real-time Tracking</div>
                    </div>
                </div>
            </div>

            {/* Dashboard Preview Section */}
            <div className="relative py-32 bg-gray-50 border-y border-gray-200 overflow-hidden">
                <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16 relative z-10">
                        <h2 className="text-sm font-bold text-amber-600 uppercase tracking-widest mb-3">The Platform</h2>
                        <h3 className="text-4xl md:text-5xl font-serif font-bold text-gray-900">Hospitality Intelligence Center</h3>
                    </div>

                    <div className="relative mx-auto max-w-5xl group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 flex items-center to-indigo-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative overflow-hidden bg-gray-900 rounded-xl ring-1 ring-gray-900/10 shadow-2xl transform transition-transform duration-700 group-hover:scale-[1.01]">
                            <div className="flex items-center px-4 py-3 border-b border-gray-800 bg-gray-900">
                                <div className="flex space-x-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                                    <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                                </div>
                            </div>
                            <img
                                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                                alt="Dashboard Analytics Preview"
                                className="w-full h-auto opacity-80 transition-opacity duration-700 group-hover:opacity-100 mix-blend-luminosity hover:mix-blend-normal object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div id="features" className="py-24 bg-gray-50 border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {/* Feature 1 */}
                        <div className="group relative">
                            <div className="aspect-w-3 aspect-h-2 overflow-hidden bg-gray-200 mb-6 shadow-none border border-gray-200">
                                <img src="https://images.unsplash.com/photo-1582719188393-bb71ca45dbb9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1474&q=80" alt="Kitchen Inventory" className="object-cover transform group-hover:scale-105 transition-transform duration-700 grayscale group-hover:grayscale-0" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2 font-serif">F&B Cost Control</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Track every bottle of wine and kilo of coffee. Instantly spot variance and shrinkage before it eats your margins.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="group relative">
                            <div className="aspect-w-3 aspect-h-2 overflow-hidden bg-gray-200 mb-6 shadow-none border border-gray-200">
                                <img src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1471&q=80" alt="Housekeeping Supplies" className="object-cover transform group-hover:scale-105 transition-transform duration-700 grayscale group-hover:grayscale-0" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2 font-serif">Housekeeping Efficiency</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Ensure your linens and amenities never run dry. Automated par level alerts keep your rooms ready for check-in.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="group relative">
                            <div className="aspect-w-3 aspect-h-2 overflow-hidden bg-gray-200 mb-6 shadow-none border border-gray-200">
                                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" alt="Manager Tablet" className="object-cover transform group-hover:scale-105 transition-transform duration-700 grayscale group-hover:grayscale-0" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2 font-serif">Executive Reporting</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Generate month-end reports in seconds, not days. Impress owners with data-driven purchasing decisions.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing Section */}
            <div id="pricing" className="py-24 bg-white border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-sm font-bold text-amber-600 uppercase tracking-widest">Plans</h2>
                        <h3 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl font-serif">Simple Pricing</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200">
                        {/* Starter */}
                        <div className="p-10 border-b md:border-b-0 md:border-r border-gray-200 hover:bg-gray-50 transition-colors">
                            <h4 className="text-lg font-bold text-gray-900 font-serif">Essentials</h4>
                            <div className="mt-4 flex items-baseline">
                                <span className="text-4xl font-bold text-gray-900">$49</span>
                                <span className="ml-1 text-gray-500">/mo</span>
                            </div>
                            <ul className="mt-8 space-y-4 text-sm text-gray-600">
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />50 AI Credits/mo</li>
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Basic Reporting</li>
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Unlimited Items</li>
                            </ul>
                            <Link to="/signup" className="mt-8 block w-full bg-gray-900 text-white text-center py-3 font-bold uppercase tracking-wider text-xs hover:bg-gray-800">Choose Plan</Link>
                        </div>

                        {/* Pro */}
                        <div className="p-10 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50 relative">
                            <div className="absolute top-0 right-0 bg-amber-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">Popular</div>
                            <h4 className="text-lg font-bold text-gray-900 font-serif">Professional</h4>
                            <div className="mt-4 flex items-baseline">
                                <span className="text-4xl font-bold text-gray-900">$149</span>
                                <span className="ml-1 text-gray-500">/mo</span>
                            </div>
                            <ul className="mt-8 space-y-4 text-sm text-gray-600">
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />200 AI Credits/mo</li>
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Advanced Analytics</li>
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Multi-User Access</li>
                            </ul>
                            <Link to="/signup" className="mt-8 block w-full bg-amber-600 text-white text-center py-3 font-bold uppercase tracking-wider text-xs hover:bg-amber-700 shadow-lg shadow-amber-900/10">Choose Plan</Link>
                        </div>

                        {/* Enterprise */}
                        <div className="p-10 hover:bg-gray-50 transition-colors">
                            <h4 className="text-lg font-bold text-gray-900 font-serif">Enterprise</h4>
                            <div className="mt-4 flex items-baseline">
                                <span className="text-4xl font-bold text-gray-900">Custom</span>
                            </div>
                            <ul className="mt-8 space-y-4 text-sm text-gray-600">
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Unlimited AI</li>
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Dedicated Support</li>
                                <li className="flex items-center"><CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />Custom Integration</li>
                            </ul>
                            <Link to="/signup" className="mt-8 block w-full border border-gray-900 text-gray-900 text-center py-3 font-bold uppercase tracking-wider text-xs hover:bg-gray-900 hover:text-white transition-colors">Contact Sales</Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Social Proof */}
            <div className="py-16 bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-8">Trusted by modern hoteliers</h2>
                    <div className="flex justify-center space-x-12 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                        {/* Mock Logos */}
                        <div className="font-bold text-2xl text-gray-700 flex items-center"><div className="w-8 h-8 bg-black rounded mr-2"></div>GrandHotel</div>
                        <div className="font-bold text-2xl text-gray-700 flex items-center"><div className="w-8 h-8 bg-blue-900 rounded-full mr-2"></div>Seaside</div>
                        <div className="font-bold text-2xl text-gray-700 flex items-center"><div className="w-8 h-8 bg-green-800 rounded-tr-xl mr-2"></div>EcoStay</div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-indigo-900 py-24 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 right-10 w-64 h-64 bg-indigo-600 rounded-full opacity-20 filter blur-3xl"></div>
                    <div className="absolute bottom-10 left-10 w-64 h-64 bg-purple-600 rounded-full opacity-20 filter blur-3xl"></div>
                </div>

                <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                        Ready to streamline your inventory?
                    </h2>
                    <p className="text-indigo-100 text-xl mb-10 max-w-2xl mx-auto">
                        Join hundreds of hotel managers who are saving time and money with Innventory AI.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <Link to="/signup" className="px-8 py-4 bg-white text-indigo-900 rounded-full font-bold text-lg hover:bg-indigo-50 transition-colors shadow-lg">
                            Get Started for Free
                        </Link>
                        <Link to="/login" className="px-8 py-4 bg-transparent border border-indigo-400 text-white rounded-full font-bold text-lg hover:bg-indigo-800/50 transition-colors">
                            Log in
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="col-span-1 md:col-span-1">
                        <span className="font-bold text-2xl text-white">Innventory<span className="text-indigo-500">AI</span></span>
                        <p className="mt-4 text-sm">
                            Making hotel inventory management intelligent, efficient, and stress-free.
                        </p>
                    </div>
                    <div>
                        <h3 className="text-white font-semibold mb-4">Product</h3>
                        <ul className="space-y-2 text-sm">
                            <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Integration</a></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-white font-semibold mb-4">Company</h3>
                        <ul className="space-y-2 text-sm">
                            <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-white font-semibold mb-4">Legal</h3>
                        <ul className="space-y-2 text-sm">
                            <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                            <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 pt-8 border-t border-gray-800 text-sm text-center">
                    © 2024 Innventory AI. All rights reserved.
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
